import type { Contribution, Item } from './types'

// ---------------------------------------------------------------------------
// Auth token management (localStorage)
// ---------------------------------------------------------------------------

const TOKEN_KEY = 'myscope_auth_token'
const USER_KEY = 'myscope_auth_user'

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function getStoredUser(): string | null {
  return localStorage.getItem(USER_KEY)
}

export function storeAuth(userId: string, token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
  localStorage.setItem(USER_KEY, userId)
}

export function clearAuth(): void {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
}

// ---------------------------------------------------------------------------
// Auth helpers
// ---------------------------------------------------------------------------

async function parseJSON<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    const msg = typeof body?.error === 'string' ? body.error : res.statusText
    throw new Error(msg)
  }
  return (await res.json()) as T
}

/** Fetch wrapper that injects Bearer token when available. */
async function authFetch(url: string, init?: RequestInit): Promise<Response> {
  const token = getStoredToken()
  const headers: Record<string, string> = {
    ...(init?.headers as Record<string, string> | undefined),
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return fetch(url, { ...init, headers })
}

// ---------------------------------------------------------------------------
// Auth API
// ---------------------------------------------------------------------------

export type RegisterResponse = { user_id: string; password: string }

/** Register a new user. Returns the generated phrase password (shown once). */
export async function registerUser(userId: string): Promise<RegisterResponse> {
  const res = await fetch('/api/v1/auth/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId }),
  })
  return parseJSON<RegisterResponse>(res)
}

export type LoginResponse = { user_id: string; token: string }

/** Authenticate and get a session token. */
export async function loginUser(userId: string, password: string): Promise<LoginResponse> {
  const res = await fetch('/api/v1/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId, password }),
  })
  return parseJSON<LoginResponse>(res)
}

/** Logout — invalidate current session token. */
export async function logoutUser(): Promise<void> {
  const res = await authFetch('/api/v1/auth/logout', { method: 'POST' })
  await parseJSON<{ message: string }>(res)
}

// ---------------------------------------------------------------------------
// Admin API (auth + admin role required)
// ---------------------------------------------------------------------------

export type AdminListUsersResponse = { users: string[] }

/** List all registered users (admin only). */
export async function adminListUsers(): Promise<AdminListUsersResponse> {
  const res = await authFetch('/api/v1/admin/users')
  return parseJSON<AdminListUsersResponse>(res)
}

export type AdminResetPasswordResponse = { user_id: string; password: string }

/** Reset a user's password and return the new phrase password (admin only). */
export async function adminResetPassword(userId: string): Promise<AdminResetPasswordResponse> {
  const res = await authFetch(`/api/v1/admin/users/${encodeURIComponent(userId)}/reset-password`, {
    method: 'POST',
  })
  return parseJSON<AdminResetPasswordResponse>(res)
}

// ---------------------------------------------------------------------------
// Data API (public)
// ---------------------------------------------------------------------------

export async function getData(limit = 200): Promise<Item[]> {
  const res = await fetch(`/api/v1/data?limit=${limit}`)
  const body = await parseJSON<{ items: Item[] }>(res)
  return body.items
}

export async function getContributions(): Promise<Contribution[]> {
  const res = await fetch('/api/v1/stats/contributions')
  const body = await parseJSON<{ contributions: Contribution[] }>(res)
  return body.contributions
}

export type CollectionSchema = {
  name: string
  label: string
  description: string
  fields: string[]
}

export async function getCollections(): Promise<CollectionSchema[]> {
  const res = await fetch('/api/v1/schema/collections')
  const body = await parseJSON<{ collections: CollectionSchema[] }>(res)
  return body.collections
}

export async function getChangelog(): Promise<string> {
  const res = await fetch('/api/v1/changelog')
  const body = await parseJSON<{ markdown: string }>(res)
  return body.markdown
}

// ---------------------------------------------------------------------------
// Test Status API (public)
// ---------------------------------------------------------------------------

export type TestStatusSummary = {
  total: number
  passed: number
  failed: number
  skipped: number
}

export type TestStatusResponse = {
  status: 'pass' | 'fail' | 'error' | 'unknown'
  last_run: string | null
  summary: TestStatusSummary
}

/** Fetch the latest E2E test run status (generated by the Docker test service). */
export async function getTestStatus(): Promise<TestStatusResponse> {
  const res = await fetch('/api/v1/test-status')
  return parseJSON<TestStatusResponse>(res)
}

// ---------------------------------------------------------------------------
// Query API (protected)
// ---------------------------------------------------------------------------

export async function runRawQuery(query: string): Promise<unknown> {
  const res = await authFetch('/api/v1/query/raw', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query }),
  })
  return parseJSON<unknown>(res)
}

export async function runBuilderQuery(payload: {
  collection: string
  fields: string[]
  filters: Array<{ field: string; op: string; value: string }>
  limit: number
}): Promise<unknown> {
  const res = await authFetch('/api/v1/query/builder', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  return parseJSON<unknown>(res)
}

export type BuilderFilter = { field: string; op: string; value: string }

export type Preset = {
  name: string
  rawQuery: string
  rawTemplate: string
  builderTemplate: string
  builderCollection: string
  builderFields: string[]
  builderFilters: BuilderFilter[]
  builderLimit: number
}

export async function getPresets(): Promise<Preset[]> {
  const res = await fetch('/api/v1/query/presets')
  const body = await parseJSON<{ presets: Preset[] }>(res)
  return body.presets
}

export async function savePreset(preset: Preset): Promise<void> {
  const res = await authFetch('/api/v1/query/presets', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(preset),
  })
  await parseJSON<{ status: string }>(res)
}

export async function deletePreset(name: string): Promise<void> {
  const res = await authFetch(`/api/v1/query/presets?name=${encodeURIComponent(name)}`, {
    method: 'DELETE',
  })
  await parseJSON<{ status: string }>(res)
}

export type RenderRequest = {
  engine: 'text' | 'html'
  template: string
  data: unknown
}

export type RenderResponse = {
  output: string
  error: string | null
}

export async function renderGoTemplate(req: RenderRequest): Promise<RenderResponse> {
  const res = await fetch('/api/v1/export/render', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  })
  return parseJSON<RenderResponse>(res)
}
